import { onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';

export interface ShortcutConfig {
  key: string;
  ctrl?: boolean;
  alt?: boolean;
  shift?: boolean;
  meta?: boolean;
  description: string;
  action: () => void;
  preventDefault?: boolean;
}

/**
 * Composable for managing keyboard shortcuts
 */
export function useKeyboardShortcuts() {
  const router = useRouter();
  const shortcuts: Map<string, ShortcutConfig> = new Map();
  const activeShortcuts: Set<string> = new Set();

  /**
   * Generate a unique key for a shortcut combination
   */
  function getShortcutKey(config: ShortcutConfig): string {
    const parts = [];
    if (config.ctrl) parts.push('ctrl');
    if (config.alt) parts.push('alt');
    if (config.shift) parts.push('shift');
    if (config.meta) parts.push('meta');
    parts.push(config.key.toLowerCase());
    return parts.join('+');
  }

  /**
   * Register a keyboard shortcut
   */
  function registerShortcut(config: ShortcutConfig) {
    const key = getShortcutKey(config);
    shortcuts.set(key, config);
    activeShortcuts.add(key);
  }

  /**
   * Unregister a keyboard shortcut
   */
  function unregisterShortcut(config: ShortcutConfig) {
    const key = getShortcutKey(config);
    activeShortcuts.delete(key);
  }

  /**
   * Check if a keyboard event matches a shortcut config
   */
  function matchesShortcut(event: KeyboardEvent, config: ShortcutConfig): boolean {
    return (
      event.key.toLowerCase() === config.key.toLowerCase() &&
      !!event.ctrlKey === !!config.ctrl &&
      !!event.altKey === !!config.alt &&
      !!event.shiftKey === !!config.shift &&
      !!event.metaKey === !!config.meta
    );
  }

  /**
   * Handle keyboard events
   */
  function handleKeyDown(event: KeyboardEvent) {
    // Don't trigger shortcuts when typing in inputs
    const target = event.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.contentEditable === 'true'
    ) {
      return;
    }

    // Check each active shortcut
    for (const key of activeShortcuts) {
      const config = shortcuts.get(key);
      if (config && matchesShortcut(event, config)) {
        if (config.preventDefault !== false) {
          event.preventDefault();
        }
        config.action();
        break;
      }
    }
  }

  /**
   * Initialize global shortcuts for the groups feature
   */
  function initializeGroupsShortcuts() {
    // Navigate to groups list
    registerShortcut({
      key: 'g',
      description: 'Go to groups list',
      action: () => router.push('/groups')
    });

    // Create new group
    registerShortcut({
      key: 'n',
      description: 'Create new group',
      action: () => router.push('/groups/new')
    });

    // Search (focus search input)
    registerShortcut({
      key: '/',
      description: 'Focus search',
      action: () => {
        const searchInput = document.querySelector('.search-input') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }
    });

    // Show keyboard shortcuts help
    registerShortcut({
      key: '?',
      shift: true,
      description: 'Show keyboard shortcuts',
      action: () => showShortcutsHelp()
    });

    // Escape - close modals/dialogs
    registerShortcut({
      key: 'Escape',
      description: 'Close dialog/modal',
      action: () => {
        // Emit a global event that dialogs can listen to
        window.dispatchEvent(new CustomEvent('close-dialog'));
      }
    });

    // Navigate between pages
    registerShortcut({
      key: 'ArrowLeft',
      alt: true,
      description: 'Previous page',
      action: () => router.back()
    });

    registerShortcut({
      key: 'ArrowRight',
      alt: true,
      description: 'Next page',
      action: () => router.forward()
    });
  }

  /**
   * Show keyboard shortcuts help dialog
   */
  function showShortcutsHelp() {
    // Create and show a help modal
    const helpModal = document.createElement('div');
    helpModal.className = 'shortcuts-help-modal';
    helpModal.innerHTML = `
      <div class="shortcuts-help-backdrop"></div>
      <div class="shortcuts-help-content">
        <h2>Keyboard Shortcuts</h2>
        <div class="shortcuts-list">
          ${Array.from(shortcuts.values())
            .filter(s => activeShortcuts.has(getShortcutKey(s)))
            .map(s => `
              <div class="shortcut-item">
                <kbd>${formatShortcut(s)}</kbd>
                <span>${s.description}</span>
              </div>
            `).join('')}
        </div>
        <button class="close-btn">Close (ESC)</button>
      </div>
    `;

    // Add styles
    const style = document.createElement('style');
    style.textContent = `
      .shortcuts-help-modal {
        position: fixed;
        inset: 0;
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .shortcuts-help-backdrop {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
      }
      .shortcuts-help-content {
        position: relative;
        background: white;
        border-radius: 12px;
        padding: 24px;
        max-width: 500px;
        max-height: 80vh;
        overflow-y: auto;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
      }
      .shortcuts-help-content h2 {
        margin: 0 0 20px 0;
        font-size: 24px;
        font-weight: 600;
      }
      .shortcuts-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-bottom: 20px;
      }
      .shortcut-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 12px;
        background: #f9fafb;
        border-radius: 6px;
      }
      .shortcut-item kbd {
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        padding: 4px 8px;
        font-family: monospace;
        font-size: 12px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      }
      .close-btn {
        width: 100%;
        padding: 10px;
        background: #3b82f6;
        color: white;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-weight: 500;
      }
      .close-btn:hover {
        background: #2563eb;
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(helpModal);

    // Close handlers
    const closeModal = () => {
      helpModal.remove();
      style.remove();
    };

    helpModal.querySelector('.shortcuts-help-backdrop')?.addEventListener('click', closeModal);
    helpModal.querySelector('.close-btn')?.addEventListener('click', closeModal);
    
    // Close on escape
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', escHandler);
      }
    };
    document.addEventListener('keydown', escHandler);
  }

  /**
   * Format shortcut for display
   */
  function formatShortcut(config: ShortcutConfig): string {
    const parts = [];
    if (config.ctrl) parts.push('Ctrl');
    if (config.alt) parts.push('Alt');
    if (config.shift) parts.push('Shift');
    if (config.meta) parts.push('⌘');
    
    // Format special keys
    let key = config.key;
    if (key === 'ArrowLeft') key = '←';
    if (key === 'ArrowRight') key = '→';
    if (key === 'ArrowUp') key = '↑';
    if (key === 'ArrowDown') key = '↓';
    if (key === 'Escape') key = 'ESC';
    
    parts.push(key.toUpperCase());
    return parts.join(' + ');
  }

  /**
   * Setup keyboard shortcuts
   */
  function setupShortcuts() {
    document.addEventListener('keydown', handleKeyDown);
  }

  /**
   * Cleanup keyboard shortcuts
   */
  function cleanupShortcuts() {
    document.removeEventListener('keydown', handleKeyDown);
    activeShortcuts.clear();
  }

  // Auto setup and cleanup
  onMounted(() => {
    setupShortcuts();
  });

  onUnmounted(() => {
    cleanupShortcuts();
  });

  return {
    registerShortcut,
    unregisterShortcut,
    initializeGroupsShortcuts,
    showShortcutsHelp,
    setupShortcuts,
    cleanupShortcuts
  };
}
